import { reactive, ref, toRaw } from 'vue'

/**
 * 统一列表状态（A层）
 *
 * 对外契约（与 Pagination/index.vue 对齐）：
 * - page: 当前页
 * - limit: 每页条数
 * - total: 总数
 * - loading: 加载态
 * - query: 查询条件（响应式对象）
 * - tableData: 表格数据
 * - search(): page=1 后拉取
 * - reset(): 重置 query + page=1 后拉取
 * - reload(): 保持 page/limit/query 拉取
 * - onPagination({ page, limit }): 翻页/改条数后拉取
 */
export default function useTableQuery(options = {}) {
  const {
    defaultQuery = {},
    immediate = true,
    buildParams,
    request,
    parseResult = res => ({ list: res?.data?.list || [], total: res?.data?.total || 0, extra: res?.data }),
    onError,
  } = options

  if (typeof request !== 'function') {
    throw new Error('useTableQuery: options.request must be a function')
  }
  if (typeof buildParams !== 'function') {
    throw new Error('useTableQuery: options.buildParams must be a function')
  }

  const query = reactive({ ...(defaultQuery || {}) })
  const page = ref(1)
  const limit = ref(20)
  const total = ref(0)
  const loading = ref(false)
  const tableData = ref([])
  const extra = ref(null)

  const doRequest = async () => {
    loading.value = true
    try {
      const params = buildParams({
        query: toRaw(query),
        page: page.value,
        limit: limit.value,
      })
      console.log('doRequest params', params)
      const res = await request(params)
      const normalized = parseResult(res) || {}
      tableData.value = normalized.list || []
      total.value = normalized.total || 0
      extra.value = normalized.extra ?? null
      return normalized
    } catch (err) {
      if (typeof onError === 'function') {
        onError(err)
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  const reload = () => doRequest()
  const search = () => {
    page.value = 1
    return doRequest()
  }
  const reset = () => {
    const next = defaultQuery || {}
    // Object.keys(query).forEach(k => {
    //   delete query[k]
    // })
    // Object.assign(query, next)
    Object.assign(query, next)
    page.value = 1
    return doRequest()
  }
  const onPagination = pagination => {
    if (pagination && pagination.page != null) page.value = pagination.page
    if (pagination && pagination.limit != null) limit.value = pagination.limit
    return doRequest()
  }

  if (immediate) {
    // 不 await，避免阻塞 setup；调用方可自行 await search/reload
    doRequest()
  }

  return {
    query,
    page,
    limit,
    total,
    loading,
    tableData,
    extra,
    reload,
    search,
    reset,
    onPagination,
  }
}
